import api from "../../../utils/api";

// ============================================================================
// CRM Prospecting — frontend service. Talks to the torre-de-control-api
// crmProspecting module. Powers the "Prospección" screen: search the web
// (Google Places) for commerce businesses in an area, review the staged
// prospects, then convert the selected ones into COMPANIES (a Places result is
// a business, not a person — it only becomes a lead once a person is added to
// the company in the Empresas directory).
//   • search  : POST /crm/prospecting/search
//   • list    : GET  /crm/prospecting/list
//   • convert : POST /crm/prospecting/convert   (→ companies)
//   • discard : POST /crm/prospecting/discard
//   • cleanup : POST /crm/prospecting/cleanup-legacy-leads
// The shared `api` axios client injects the auth token + session headers.
// ============================================================================

export type ProspectStatus = "pending" | "converted" | "discarded";

/** Firestore Timestamp as serialized over JSON (admin SDK shape). */
export interface FirestoreTimestamp {
  _seconds?: number;
  _nanoseconds?: number;
  seconds?: number;
  nanoseconds?: number;
}

/** A staged business prospect found by a search, pending review/conversion. */
export interface Prospect {
  id: string;
  searchId: string | null;
  keyword: string | null;
  area: string | null;
  status: ProspectStatus;
  name: string | null;
  phone: string | null;
  whatsapp: string | null;
  website: string | null;
  address: string | null;
  /** State / province, extracted individually from the place's address. */
  state: string | null;
  /** Country, extracted individually from the place's address. */
  country: string | null;
  category: string | null;
  location: { lat: number; lng: number } | null;
  rating: number | null;
  ratingsCount: number | null;
  businessStatus: string | null;
  placeId: string | null;
  source: string | null;
  /** Product the batch was prospecting for; rides onto the resulting company. */
  product: string | null;
  /** Set once the prospect has been converted into a company. */
  companyId: string | null;
  createdAt?: FirestoreTimestamp | string | null;
}

/** Params for a search run. Provide either `place` text OR explicit lat/lng. */
export interface ProspectSearchParams {
  keyword: string;
  place?: string;
  lat?: number;
  lng?: number;
  /** Search radius in kilometers (0.1 – 50). */
  radius?: number;
  /** The fixable maximum number of leads to fetch (1 – 120). */
  maxResults?: number;
  source?: string;
  /** Product the resulting companies are prospects for (Coding Cloud/Wayak/Nuvex). */
  product?: string;
}

export interface ProspectSearchResult {
  searchId: string;
  area: string | null;
  keyword: string;
  message: string;
  /** Total results returned by the source (before duplicate filtering). */
  total: number;
  /** How many fresh prospects were staged. */
  saved: number;
  /** How many were skipped as already-seen duplicates. */
  duplicates: number;
  prospects: Prospect[];
}

export interface ProspectsPage {
  data: Prospect[];
  totalItems: number;
  totalPages: number;
  currentPage: number;
}

export interface ConvertResult {
  message: string;
  total: number;
  created: number;
  skipped: number;
  errors: { id: string; reason: string }[];
}

export interface DiscardResult {
  message: string;
  total: number;
  discarded: number;
  errors: { id: string; reason: string }[];
}

/** Run a web-prospecting search and stage the results for review. */
export async function searchProspects(
  params: ProspectSearchParams,
): Promise<ProspectSearchResult> {
  const res = await api.post("/crm/prospecting/search", params);
  return res.data?.data;
}

interface ProspectsListParams {
  page?: number;
  limit?: number;
  status?: ProspectStatus;
  searchId?: string;
}

/** List staged prospects (newest first), optionally filtered. */
export async function listProspects(
  params: ProspectsListParams = {},
): Promise<ProspectsPage> {
  const { page = 1, limit = 50, status, searchId } = params;
  const res = await api.get("/crm/prospecting/list", {
    params: {
      page,
      limit,
      ...(status ? { status } : {}),
      ...(searchId ? { searchId } : {}),
    },
  });
  const body = res.data ?? {};
  return {
    data: Array.isArray(body.data) ? body.data : [],
    totalItems: body.meta?.totalItems ?? 0,
    totalPages: body.meta?.totalPages ?? 1,
    currentPage: body.meta?.currentPage ?? page,
  };
}

/** Convert the selected prospects into COMPANIES (created/skipped count them). */
export async function convertProspects(ids: string[]): Promise<ConvertResult> {
  const res = await api.post("/crm/prospecting/convert", { ids });
  return res.data?.data;
}

/** Discard the selected prospects (marks them as discarded). */
export async function discardProspects(ids: string[]): Promise<DiscardResult> {
  const res = await api.post("/crm/prospecting/discard", { ids });
  return res.data?.data;
}

export interface CleanupResult {
  message: string;
  total: number;
  removed: number;
  companies: number;
  errors: { id: string; reason: string }[];
}

/**
 * One-time migration: remove the leads created by the OLD prospecting convert
 * (source "prospecting"), leaving only their companies.
 */
export async function cleanupLegacyLeads(): Promise<CleanupResult> {
  const res = await api.post("/crm/prospecting/cleanup-legacy-leads", {});
  return res.data?.data;
}
